/**
 * SortableServiceCard Component
 *
 * Thin wrapper around ExpandableCard that adds drag-and-drop
 * reordering support via @dnd-kit.
 */

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { ExpandableCard } from '@/components/common/ExpandableCard';

interface SortableServiceCardProps {
  id: string;
  showHandle: boolean;
  expanded: boolean;
  onToggle: () => void;
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** Tint the card background slightly, for zebra-striping alternate rows in the list */
  striped?: boolean;
}

export function SortableServiceCard({
  id,
  showHandle,
  expanded,
  onToggle,
  title,
  actions,
  children,
  striped = false,
}: SortableServiceCardProps) {
  const { t: tc } = useTranslation('common');
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-booking-service-id={id}
      sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}
    >
      {showHandle && (
        <Box
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            touchAction: 'none',
            color: 'action.active',
            '&:hover': { color: 'text.primary' },
            '&:active': { cursor: 'grabbing' },
          }}
          aria-label={tc('dragToReorder')}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ExpandableCard
          expanded={isDragging ? false : expanded}
          onToggle={onToggle}
          title={title}
          actions={actions}
          striped={striped}
        >
          {children}
        </ExpandableCard>
      </Box>
    </Box>
  );
}
